import type { ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { ProductSheetDTO } from "@easyshop/shared";
import { ErrorState, Loading, cx } from "@/components/ui";
import { IosSection } from "@/components/mobile/MobileUi";
import { LevelBadge, PCard, StatRow, StatTile, coverage, stockLevel } from "@/components/pc/Kit";
import { dateShort } from "@/components/planning/utils";
import { PageHeader, PageStack } from "@/layouts/DesktopShell";
import { useSession } from "@/hooks/useSession";
import { useProductSheet } from "@/lib/queries";
import { dayMonth, eur, eur2, num } from "@/lib/format";

/** Retour contextuel transmis par `openProduct` (« ‹ Lyon · Stock », « ‹ Produits »…). */
export interface ProductBack {
  label: string;
  to: string;
}

const PROMO_STATUS = {
  ACTIVE: ["bg-positive-bg text-positive", "En cours"],
  PLANNED: ["bg-accent-tint text-accent", "À venir"],
  DONE: ["bg-ios-fill text-muted", "Terminée"],
} as const;

/** Fiche produit globale (PC) : chiffres du périmètre, ventes par semaine, promos, détail par boutique, arrivages. */
export function ProductSheetPage() {
  const [params] = useSearchParams();
  const loc = useLocation();
  // `nom` : anciens liens, et produits pas encore rattachés à leur produit réseau.
  const id = params.get("id");
  const name = params.get("nom");
  const focus = params.get("boutique") ?? undefined;
  const back = (loc.state as { back?: ProductBack } | null)?.back ?? { label: "Produits", to: "/produits" };
  const q = useProductSheet({ id, name }, focus);
  const backLink = (
    <Link to={back.to} className="mb-1 inline-block whitespace-nowrap text-[15px] text-accent hover:underline">
      ‹ {back.label}
    </Link>
  );
  if (!id && !name) return <ErrorState error={new Error("Produit introuvable.")} />;
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorState error={q.error} />;
  return q.data ? <Sheet p={q.data} focus={focus} backLink={backLink} /> : null;
}

function Sheet({ p, focus, backLink }: { p: ProductSheetDTO; focus?: string; backLink: ReactNode }) {
  const { me } = useSession();
  const navigate = useNavigate();
  const multi = p.boutiques.length > 1;
  const active = p.promos.find((x) => x.status === "ACTIVE");
  const badge = active ? `${active.name}${active.discountPct != null ? ` −${active.discountPct} %` : ""}` : p.promo?.label;
  const cities = p.boutiques.map((b) => b.city).join(", ");
  const scopeLabel = me.networkWide ? "réseau" : multi ? "mes boutiques" : (p.boutiques[0]?.city ?? "");

  return (
    <PageStack>
      <PageHeader
        back={backLink}
        title={p.name}
        badge={badge && <span className="whitespace-nowrap rounded-[6px] bg-accent px-2 py-[3px] text-[12.5px] font-semibold text-white">{badge}</span>}
        subtitle={[
          p.subCategory ?? p.category,
          p.price != null ? `${p.supplyPrice != null ? `achat ${eur2(p.supplyPrice)} → ` : ""}vente ${eur2(p.price)}` : null,
          p.marginPct != null ? `marge ${Math.round(p.marginPct)} %` : null,
        ]
          .filter(Boolean)
          .join(" · ")}
      />

      <StatRow>
        <StatTile
          label={me.networkWide ? "Stock réseau" : multi ? "Stock mes boutiques" : "Stock"}
          value={`${num(p.stockTotal)} u.`}
          tone={p.boutiquesRupture > 0 ? "danger" : undefined}
          hint={multi ? `${num(p.boutiquesRupture)} boutique(s) en rupture` : p.stockCostValue != null ? `${eur(p.stockCostValue)} au prix d'achat` : undefined}
        />
        <StatTile label={`Vendu ${p.windowDays} j`} value={`${num(p.soldTotal)} u.`} hint={me.networkWide ? "toutes boutiques" : cities} />
        <StatTile label="Couverture" value={coverage(p.coverageDays)} hint="au rythme actuel" />
        <StatTile label={`CA ${p.windowDays} j`} value={eur(p.revenueWindow)} hint={p.marginPct != null ? `marge ${Math.round(p.marginPct)} %` : undefined} />
      </StatRow>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <IosSection title={`Ventes par semaine — ${scopeLabel}`}>
          <WeeklyBars weeks={p.weeklySales} />
        </IosSection>
        <IosSection title="Remises & promos">
          <PCard className="overflow-hidden">
            {p.promos.length === 0 && <div className="px-4 py-4 text-[15px] text-muted">Aucune promo récente ou prévue.</div>}
            {p.promos.map((x) => {
              const [cls, label] = PROMO_STATUS[x.status];
              return (
                <div key={x.id} className="flex items-center gap-3 border-b-[0.5px] border-ios-sep px-4 py-[11px] last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-[6px]">
                      <span className="text-[15px] font-semibold">{x.name}</span>
                      {x.discountPct != null && <span className="rounded-[6px] bg-accent px-[6px] py-px text-[12px] font-semibold text-white">−{x.discountPct} %</span>}
                      <span className={cx("rounded-[6px] px-[6px] py-px text-[12px] font-semibold", cls)}>{label}</span>
                    </div>
                    <div className="text-[13px] text-muted">{x.startDate === x.endDate ? dateShort(x.startDate) : `${dateShort(x.startDate)} → ${dateShort(x.endDate)}`}</div>
                  </div>
                  {x.discountPct != null && p.price != null && (
                    <div className="flex-shrink-0 text-right">
                      <div className="text-[15px] font-semibold tabular">{eur2(p.price * (1 - x.discountPct / 100))}</div>
                      <div className="text-[12px] tabular text-muted line-through">{eur2(p.price)}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </PCard>
        </IosSection>
      </div>

      {(multi || p.networkAverage) && (
        <IosSection title={me.networkWide ? "Par boutique" : "Mes boutiques vs réseau"}>
          <ByBoutique p={p} focus={focus} onOpen={(id) => navigate(`/boutiques/${id}?tab=stock`)} />
        </IosSection>
      )}

      {p.arrivals.length > 0 && (
        <IosSection title="Derniers arrivages de ce produit">
          <PCard className="overflow-hidden">
            {p.arrivals.map((a, i) => (
              <div key={i} className="flex min-h-[48px] items-center gap-4 border-b-[0.5px] border-ios-sep px-4 last:border-b-0">
                <span className="w-[64px] flex-shrink-0 text-[14px] text-muted">{a.date ? dayMonth(a.date) : "—"}</span>
                <span className="min-w-0 flex-1 truncate text-[15px]">{a.supplier ?? "Sans fournisseur"}</span>
                {multi && <span className="flex-shrink-0 text-[13px] text-muted">{a.city}</span>}
                <span className="w-[80px] flex-shrink-0 text-right text-[15px] font-semibold tabular">{num(a.quantity)} u.</span>
              </div>
            ))}
          </PCard>
        </IosSection>
      )}
    </PageStack>
  );
}

/** 12 semaines de ventes en barres (la semaine en cours en vert foncé). */
function WeeklyBars({ weeks }: { weeks: ProductSheetDTO["weeklySales"] }) {
  const max = Math.max(1, ...weeks.map((w) => w.quantity));
  return (
    <PCard className="px-4 pb-3 pt-4">
      <div className="flex h-[170px] items-end gap-[6px]">
        {weeks.map((w, i) => (
          <div key={w.from} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end" title={`Semaine ${w.week} · dès le ${dateShort(w.from)}`}>
            <span className="mb-1 text-[11px] tabular text-muted">{num(w.quantity)}</span>
            <div
              className={cx("w-full rounded-t-[4px]", i === weeks.length - 1 ? "bg-accent" : "bg-[oklch(0.8_0.05_152)]")}
              style={{ height: `${Math.max(2, (w.quantity / max) * 130)}px` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-[6px] flex gap-[6px]">
        {weeks.map((w) => (
          <span key={w.from} className="min-w-0 flex-1 text-center text-[11px] text-muted">
            S{w.week}
          </span>
        ))}
      </div>
    </PCard>
  );
}

/** Tableau par boutique : la boutique d'origine surlignée, total, moyenne réseau anonymisée (gérant). */
function ByBoutique({ p, focus, onOpen }: { p: ProductSheetDTO; focus?: string; onOpen: (id: string) => void }) {
  const { me } = useSession();
  const rows = [...p.boutiques].sort((a, b) => b.sold - a.sold);
  const avg = p.networkAverage;
  const max = Math.max(1, ...rows.map((r) => r.sold), avg?.sold ?? 0);
  const totalSold = rows.reduce((s, r) => s + r.sold, 0);
  const totalStock = rows.reduce((s, r) => s + (r.stock ?? 0), 0);
  const totalRevenue = rows.reduce((s, r) => s + r.revenue, 0);
  const th = "px-4 py-[10px] text-[12px] font-medium text-muted";
  const bar = (n: number, color: string) => (
    <div className="flex items-center gap-3">
      <div className="h-[5px] min-w-[80px] flex-1 overflow-hidden rounded-[3px] bg-ios-fill">
        <div className={cx("h-full", color)} style={{ width: `${(n / max) * 100}%` }} />
      </div>
      <span className="w-[56px] text-right text-[14px] tabular">{num(n)}</span>
    </div>
  );
  return (
    <PCard className="overflow-x-auto">
      <table className="w-full min-w-[760px] border-collapse">
        <thead>
          <tr className="border-b-[0.5px] border-ios-sep">
            <th className={cx(th, "text-left")}>Boutique</th>
            <th className={cx(th, "w-[34%] text-left")}>Vendu {p.windowDays} j</th>
            <th className={cx(th, "text-right")}>Stock</th>
            <th className={cx(th, "text-right")}>Couverture</th>
            <th className={cx(th, "text-right")}>CA {p.windowDays} j</th>
            <th className={cx(th, "text-right")}>Statut</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const franchise = r.type === "FRANCHISE";
            const here = r.boutiqueId === focus;
            return (
              <tr
                key={r.boutiqueId}
                onClick={() => onOpen(r.boutiqueId)}
                className={cx("cursor-pointer border-b-[0.5px] border-ios-sep", here ? "bg-[oklch(0.96_0.03_152)]" : "hover:bg-[oklch(0.975_0.004_145)]")}
              >
                <td className="px-4 py-[11px]">
                  <span className="flex items-center gap-2">
                    <span className={cx("h-2 w-2 flex-shrink-0 rounded-full", franchise ? "bg-franchise" : "bg-positive")} />
                    <span className="text-[15px]">{r.city}</span>
                    {here && <span className="text-[11.5px] font-semibold text-accent">vous venez d'ici</span>}
                  </span>
                  {/* Rattachement à vérifier d'un coup d'œil : le nom du produit dans cette caisse. */}
                  {r.localName && r.localName !== p.name && (
                    <span className="mt-[2px] block pl-4 text-[12px] text-muted">En caisse : {r.localName}</span>
                  )}
                </td>
                <td className="px-4">{bar(r.sold, franchise ? "bg-franchise" : "bg-positive")}</td>
                <td className="px-4 text-right text-[15px] tabular">{r.found ? num(r.stock) : "—"}</td>
                <td className="px-4 text-right text-[14px] tabular">{coverage(r.coverageDays)}</td>
                <td className="px-4 text-right text-[14px] tabular">{eur(r.revenue)}</td>
                <td className="px-4 text-right">{r.found ? <LevelBadge level={stockLevel(r.stock, r.coverageDays)} /> : <span className="text-[12px] text-muted">Non référencé</span>}</td>
              </tr>
            );
          })}
          {rows.length > 1 && (
            <tr className="border-b-[0.5px] border-ios-sep bg-[oklch(0.975_0.004_145)] font-semibold">
              <td className="px-4 py-[11px] text-[15px]">{me.networkWide ? "Total réseau" : "Total mes boutiques"}</td>
              <td className="px-4">{bar(totalSold, "bg-transparent")}</td>
              <td className="px-4 text-right text-[15px] tabular">{num(totalStock)}</td>
              <td className="px-4 text-right text-[14px] tabular">{coverage(p.coverageDays)}</td>
              <td className="px-4 text-right text-[14px] tabular">{eur(totalRevenue)}</td>
              <td />
            </tr>
          )}
          {avg && (
            <tr className="text-muted">
              <td className="px-4 py-[11px]">
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 flex-shrink-0 rounded-full bg-[oklch(0.75_0.006_150)]" />
                  <span className="text-[15px] text-ink">Moyenne réseau</span>
                  <span className="text-[11.5px] font-semibold text-accent">anonymisée</span>
                </span>
              </td>
              <td className="px-4">{bar(Math.round(avg.sold), "bg-[oklch(0.75_0.006_150)]")}</td>
              <td className="px-4 text-right text-[15px] tabular">{num(Math.round(avg.stock))}</td>
              <td className="px-4 text-right text-[14px] tabular">{coverage(avg.coverageDays)}</td>
              <td className="px-4 text-right text-[14px] tabular">{eur(avg.revenue)}</td>
              <td />
            </tr>
          )}
        </tbody>
      </table>
    </PCard>
  );
}
